// When a DoseSpot connect form (clinician setup, proxy setup, patient connect)
// is rejected, the backend surfaces DoseSpot's own reason string. That string is
// accurate but reads like a machine dump — e.g.
//   "DoseSpot rejected clinician setup: Property: Phone.Number Details: Phone of
//    type Fax and number [3133333333] is not a valid phone number."
// This maps the known DoseSpot rejection reasons into clinician-friendly copy,
// and — for anything it doesn't recognize — strips the vendor scaffolding so the
// provider at least sees a plain sentence instead of a dict/property dump.
//
// It is deliberately tolerant of every message shape the backend has produced:
//   - post-GDN-2378: "DoseSpot rejected <action>: Property: <Field> Details: <reason>"
//   - post-GDN-2378 (HTTP 400 ModelState): "DoseSpot rejected <Field>: <reason>"
//   - pre-GDN-2378 (raw dict): "... {'Result': {'ResultDescription': '<reason>'}}"
// so it works whether or not the backend change has shipped. Non-DoseSpot
// messages pass through untouched.

/**
 * Turn a raw DoseSpot connect-rejection reason into clinician-friendly copy.
 *
 * Returns curated copy for known field rejections (fax/phone/DOB/NPI/address/ZIP),
 * a de-scaffolded sentence for other DoseSpot reasons, or the original string
 * unchanged when it isn't a DoseSpot rejection.
 */
export function humanizeDoseSpotError(raw: string): string {
  if (!raw) return raw;

  const reason = extractReason(raw);

  const curated = curatedCopy(reason);
  if (curated) return curated;

  // We unwrapped a DoseSpot reason but don't have curated copy for it — show the
  // plain sentence (scaffolding stripped) rather than the raw property dump.
  if (reason !== raw) return cleanReason(reason);

  // Not a recognizable DoseSpot rejection — leave the message as-is.
  return raw;
}

/**
 * Pull the deepest human-readable reason out of the backend's wrappers:
 * the vendor `ResultDescription`, or the text after our own
 * "DoseSpot rejected <action>:" / "DoseSpot did not return ...:" prefix.
 */
function extractReason(raw: string): string {
  const resultDescription = raw.match(
    /ResultDescription['"]?\s*:\s*['"]([^'"]+)['"]/,
  );
  if (resultDescription) return resultDescription[1].trim();

  const rejected = raw.match(/^DoseSpot (?:rejected|did not return)[^:]*:\s*(.+)$/s);
  if (rejected) return rejected[1].trim();

  return raw;
}

/** Curated, friendly copy for the DoseSpot rejections we've actually seen. */
function curatedCopy(reason: string): string | null {
  const r = reason.toLowerCase();
  const value = extractPhoneValue(reason);

  const looksInvalid = /(not a valid|is not valid|invalid|not valid)/.test(r);

  // Fax must be checked before phone — DoseSpot phrases it as "Phone of type Fax".
  if (/fax/.test(r) && (looksInvalid || /phone number/.test(r))) {
    return value
      ? `The fax number (${value}) isn't valid. Please double-check it and enter a valid 10-digit fax number.`
      : `That fax number isn't valid. Please enter a valid 10-digit fax number.`;
  }

  if (/phone/.test(r) && looksInvalid) {
    return value
      ? `The phone number (${value}) isn't valid. Please enter a valid 10-digit phone number.`
      : `That phone number isn't valid. Please enter a valid 10-digit phone number.`;
  }

  if (/(dob|date of birth)/.test(r)) {
    if (/(past|too old|range)/.test(r)) {
      return `That date of birth looks too far in the past. Please double-check it.`;
    }
    if (looksInvalid || /valid date/.test(r)) {
      return `Please enter the date of birth as a valid calendar date.`;
    }
  }

  if (/npi/.test(r) && looksInvalid) {
    return `That NPI doesn't look valid. Please check the 10-digit NPI number.`;
  }

  if (/address/.test(r) && /(length|too long|35|character)/.test(r)) {
    return `That street address is too long for e-prescribing. Please shorten it to 35 characters or fewer.`;
  }

  if (/(zip|postal)/.test(r) && (looksInvalid || /format/.test(r))) {
    return `That ZIP code isn't valid. Please enter a valid 5-digit ZIP code.`;
  }

  return null;
}

/**
 * Strip DoseSpot's "Property: <Field> Details:" scaffolding, leaving the plain
 * sentence. Returns the reason unchanged when there's no scaffolding to strip.
 */
function cleanReason(reason: string): string {
  const afterDetails = reason.match(/Details:\s*(.+)$/s);
  return (afterDetails ? afterDetails[1] : reason).trim();
}

/**
 * Extract a bracketed phone-like value (e.g. "[3133333333]") and format it as
 * 3-3-4 when it's a 10-digit number. Returns null when there's nothing to format.
 */
function extractPhoneValue(reason: string): string | null {
  const bracketed = reason.match(/\[([\d\s\-().]{7,})\]/);
  if (!bracketed) return null;
  const digits = bracketed[1].replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits || null;
}
